import { useTranslation } from "react-i18next";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Container,
  Typography,
  Paper,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from "@mui/material";
import FeedbackIcon from "@mui/icons-material/Feedback";
import GetAppIcon from "@mui/icons-material/GetApp";
import BugReportIcon from "@mui/icons-material/BugReport";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import { useAuth } from "./AuthContext";

function BetaPrivateHub() {
  const { currentUser, userRole } = useAuth();
  const { t } = useTranslation();
  const playStoreUrl =
    "https://play.google.com/store/apps/details?id=com.eil.kbquick";
  const appStoreUrl = "https://apps.apple.com/app/kb-quick/id6757280561";

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("betaHub.title")}
      </Typography>
      <Typography variant="h6" color="text.secondary" paragraph>
        {t("betaHub.welcome", { name: currentUser?.displayName })}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "left", fontStyle: "italic", mb: 3 }}
      >
        {t("betaHub.installTip")}
      </Typography>

      {userRole === "free_user" && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("home.freeWebNotice")}
        </Alert>
      )}

      {/* Download Section - Two columns. Each Paper is a flex column with the
          badge bottom-anchored (mt: auto) and centered in an equal-height box,
          so the Google Play (60px) and App Store (40px) badges line up. */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <AndroidIcon sx={{ fontSize: 32, mr: 1, color: "#3DDC84" }} />
              <Typography variant="h5" component="h2">
                Android
              </Typography>
            </Box>
            <Typography paragraph>{t("betaHub.androidDownload")}</Typography>
            <Box sx={{ mt: "auto", display: "flex", alignItems: "center", minHeight: 60 }}>
              <Link href={playStoreUrl} target="_blank" rel="noopener">
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  style={{ height: "60px" }}
                />
              </Link>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <AppleIcon sx={{ fontSize: 32, mr: 1, color: "#555" }} />
              <Typography variant="h5" component="h2">
                iPhone
              </Typography>
            </Box>
            <Typography paragraph>{t("betaHub.iphoneDownload")}</Typography>
            <Box sx={{ mt: "auto", display: "flex", alignItems: "center", minHeight: 60 }}>
              <Link href={appStoreUrl} target="_blank" rel="noopener">
                <img
                  src="/assets/images/app-store-badge.svg"
                  alt="Download on the App Store"
                  style={{ height: "40px" }}
                />
              </Link>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Instructions Section */}
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {t("betaHub.understanding")}
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <GetAppIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={t("betaHub.useMobilePrimary")}
              secondary={
                <>
                  {t("betaHub.useMobileSecondary")}{" "}
                  <Link href="#faq" underline="always">
                    {t("betaHub.seeFaqBelow")}
                  </Link>
                  .
                </>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <BugReportIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={t("betaHub.reportBugsPrimary")}
              secondary={
                <>
                  {t("betaHub.reportBugsSecondary1")}{" "}
                  <Link component={RouterLink} to="/contact" underline="always">
                    {t("betaHub.contactUs")}
                  </Link>{" "}
                  {t("betaHub.reportBugsSecondary2")}
                  <FeedbackIcon
                    sx={{
                      fontSize: "1rem",
                      verticalAlign: "middle",
                      mx: 0.25,
                    }}
                  />
                  {t("betaHub.reportBugsSecondary3")}
                </>
              }
            />
          </ListItem>
        </List>
      </Paper>

      {/* FAQ Section */}
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        sx={{ mt: 4 }}
        id="faq"
      >
        {t("betaHub.faq")}
      </Typography>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{t("betaHub.faqImageQuality")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" component="p" sx={{ mb: 2 }}>
              {t("betaHub.faqImageQualityA1")}
            </Typography>
            <Typography variant="body2" component="p" sx={{ mb: 2 }}>
              {t("betaHub.faqImageQualityA2")}
            </Typography>
            <Typography variant="body2" component="p" sx={{ mb: 2 }}>
              {t("betaHub.faqImageQualityA3")}
            </Typography>
            <Typography variant="body2" component="p" sx={{ mb: 2 }}>
              {t("betaHub.faqImageQualityA4")}{" "}
              <Link href="/assets/images/aruco-funnel-100mm.pdf" download>
                {t("betaHub.faq100mmPlate")}
              </Link>{" "}
              {t("betaHub.faqOr")}{" "}
              <Link href="/assets/images/aruco-funnel-150mm.pdf" download>
                {t("betaHub.faq150mmPlate")}
              </Link>
              .
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{t("betaHub.faqData")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {t("betaHub.faqDataA")}{" "}
              <Link component={RouterLink} to="/privacy" underline="always">
                {t("betaHub.privacyPolicy")}
              </Link>
              .
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{t("betaHub.faqOffline")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>{t("betaHub.faqOfflineA")}</Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{t("betaHub.faqPermissions")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>{t("betaHub.faqPermissionsA")}</Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{t("betaHub.faqTestFlight")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {t("betaHub.faqTestFlightA")}
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{t("betaHub.faqDeveloper")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {t("betaHub.faqDeveloperA")}{" "}
              <Link
                href="https://epi-insight.com"
                target="_blank"
                rel="noopener"
                underline="always"
              >
                Epi-Insight Limited
              </Link>
              {t("betaHub.faqDeveloperA2")}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Training Aids Section */}
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t("betaHub.trainingAids")}
        </Typography>

        {/* Videos */}
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t("betaHub.videos")}
        </Typography>
        <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
          <li>
            <Typography variant="body1">
              <a
                href="/assets/training/KB Quick - Joining an Organisation.mp4"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("betaHub.videoJoining")}
              </a>
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <a
                href="/assets/training/KB Quick - Add or Delete Disks.mp4"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("betaHub.videoDisks")}
              </a>
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              <a
                href="/assets/training/KB Quick - Metadata and Disk Assessment.mp4"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("betaHub.videoMetadata")}
              </a>
            </Typography>
          </li>
        </ul>

        {/* Presentations */}
        <Typography variant="h6" sx={{ mt: 3 }}>
          {t("betaHub.presentations")}
        </Typography>
        <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
          <li>
            <Typography variant="body1">
              <a
                href="/assets/training/kbquick intro_13Nov2025.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("betaHub.presentationIntro")}
              </a>
            </Typography>
          </li>
        </ul>
      </Paper>
    </Container>
  );
}

export default BetaPrivateHub;
